import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import InitialsBackground from "./InitialsBackground";
import Node from "./Node";

const NODE_COUNT = 16;

const generateRadialPosition = (index: number) => {
  if (index === 0) return { x: 0, y: 0 };

  const ring = Math.ceil(Math.sqrt(index));
  const angle = (index * 137.5) % 360;
  const radius = ring * 200;
  const x = Math.cos((angle * Math.PI) / 180) * radius;
  const y = Math.sin((angle * Math.PI) / 180) * radius;

  return { x, y };
};

const NodeGrid = () => {
  const [activeId, setActiveId] = useState(-1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState<number>(1);

  useEffect(() => {
    const handleWheel = (event: WheelEvent) => {
      if (event.ctrlKey) {
        event.preventDefault();
        setScale((prevScale) => {
          const newScale = event.deltaY < 0 ? prevScale + 0.1 : prevScale - 0.1;
          return Math.max(0.5, Math.min(newScale, 5));
        });
      }
    };

    const handleKeyZoom = (event: KeyboardEvent) => {
      if (event.key === "+" || event.key === "=") {
        event.preventDefault();
        setScale((prevScale) => {
          const newScale = prevScale + 0.1;
          return Math.max(0.5, Math.min(newScale, 5));
        });
      } else if (event.key === "-") {
        event.preventDefault();
        setScale((prevScale) => {
          console.log({ prevScale });
          const newScale = prevScale - 0.1;
          return Math.max(0.5, Math.min(newScale, 5));
        });
      }
    };

    window.addEventListener("wheel", handleWheel);
    window.addEventListener("keydown", handleKeyZoom);

    return () => {
      window.removeEventListener("wheel", handleWheel);
      window.addEventListener("keydown", handleKeyZoom);
    };
  }, []);

  const nodes = Array.from({ length: NODE_COUNT }, (_, i) => {
    const pos = generateRadialPosition(i);
    return {
      id: i,
      ...pos,
      video: "https://www.w3schools.com/html/mov_bbb.mp4",
      thumbnail:
        "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRxmcXzNzv3Ie-eSh8yfs6helV2BSC2mBaCLctucAkUvAqU4JSP08kBQtAoS10e__uDs8Q",
    };
  });

  const focusOn = (node: any) => {
    setActiveId(node.id);
    // setPan({ x: -(node.x / 2), y: -(node.y / 2) });
    setPan({ x: -node.x, y: -node.y });
  };

  const handleBlur = () => {
    setActiveId(-1);
    setPan({ x: 0, y: 0 });
    setScale(1);
  };

  return (
    <Wrapper scale={scale}>
      <InitialsBackground />
      <DragCanvas
        drag
        dragElastic={0.15}
        dragConstraints={{ top: -800, bottom: 800, left: -800, right: 800 }}
        animate={{ x: pan.x, y: pan.y }}
        transition={{ type: "spring", stiffness: 120, damping: 22 }}
        ref={canvasRef}
      >
        <AnimatePresence>
          {nodes.map((node) => {
            const dx = node.x + pan.x;
            const dy = node.y + pan.y;
            const dist = Math.sqrt(dx * dx + dy * dy);

            let size = "small";
            if (dist < 120) size = "large";
            else if (dist < 300) size = "medium";

            return (
              <motion.div
                key={node.id}
                style={{
                  position: "absolute",
                  left: `calc(50% + ${node.x}px)`,
                  top: `calc(50% + ${node.y}px)`,
                }}
                initial={{ opacity: 0, scale: 0.3 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: node.id * 0.03 }}
              >
                <Node
                  node={node}
                  size={size}
                  isActive={node.id === activeId}
                  onClick={() => focusOn(node)}
                  onBlur={() => handleBlur()}
                />
              </motion.div>
            );
          })}
        </AnimatePresence>
      </DragCanvas>
    </Wrapper>
  );
};

export default NodeGrid;

const Wrapper = styled.div<{ scale: number }>`
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background: #fff;
  transform: scale(${(props) => props.scale});
  transform-origin: center;
  transition: transform 0.3s ease, padding 0.3s ease;
  padding-left: ${(props) =>
    props.scale === 1 ? 0 : (1 / props.scale) * 20}px;
  padding-right: ${(props) =>
    props.scale === 1 ? 0 : (1 / props.scale) * 20}px;
`;

const DragCanvas = styled(motion.div)`
  width: 4000px;
  height: 4000px;
  position: absolute;
  left: -1565px;
  top: -1865px;
  cursor: grab;

  @media only screen and (max-width: 650px) {
    left: -1970px;
    top: -1900px;
  }
`;
